import React from 'react';
import PropTypes from 'prop-types';
import {Scrollbars} from 'react-custom-scrollbars';
import getEditDistance from '../../services/utils/getEditDistance';
import classNames from 'classnames';

import '../../assets/styles/components/input';

const TRUSTED_DOMAINS = ['gmail.com', 'mail.ru', 'yandex.ru', 'inbox.ru', 'rambler.ru', 'icloud.com',
    'list.ru', 'hotmail.com', 'yahoo.com', 'qip.ru', 'borda.ru', 'pochta.ru', 'fromru.com',
    'front.ru', 'hotbox.ru', 'hotmail.ru', 'krovatka.su', 'land.ru', 'mail15.com', 'mail333.com',
    'newmail.ru', 'nightmail.ru', 'nm.ru', 'pisem.net', 'pochtamt.ru', 'pop3.ru', 'rbcmail.ru', 'smtp.ru',
    '5ballov.ru', 'aeterna.ru', 'ziza.ru', 'memori.ru', 'photofile.ru', 'fotoplenka.ru', 'pochta.com', 'webmail.ru',
];

export class EmailField extends React.Component {
    state = {
        listOpened: false,
        items: [],
        activeItem: 0,
        itemsCount: 0,
        isActive: false,
    };

    componentDidMount() {
        document.addEventListener('click', this.handleClickOutsideSuggestList);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutsideSuggestList);
    }

    getEmailsDomains = value => {
        if (value.indexOf('@') === -1) {
            return [];
        }

        const splitValue = value.split('@');
        const domain = splitValue[1];

        if (domain === '' || splitValue[0] === '') {
            return [];
        }

        const {approximateEmailsList, exactEmailsList} = TRUSTED_DOMAINS.reduce((acc, trustedDomain) => {
            if (trustedDomain.startsWith(domain) && trustedDomain !== domain) {
                acc.approximateEmailsList.push(`${splitValue[0]}@${trustedDomain}`);

                return acc;
            }

            if (!trustedDomain.startsWith(domain) && getEditDistance(trustedDomain.substring(0, domain.length - 1), domain) >= .85) {
                acc.exactEmailsList.push(`${splitValue[0]}@${trustedDomain}`);

                return acc;
            }

            return acc;
        }, {approximateEmailsList: [], exactEmailsList: []});

        return exactEmailsList.concat(approximateEmailsList);
    };

    static keyDownHandlers = {
        Escape() {
            this.setState({listOpened: false});
        },
        ArrowUp() {
            if (!this.scroll) {
                return;
            }

            const {activeItem, itemsCount} = this.state;

            if (activeItem !== 0) {
                this.setState({activeItem: activeItem - 1});
                this.scroll.scrollTop(this.scroll.getScrollTop() - 36);
            } else {
                this.setState({activeItem: itemsCount - 1});
                this.scroll.scrollToBottom();
            }
        },
        ArrowDown() {
            if (!this.scroll) {
                return;
            }

            const {itemsCount, activeItem} = this.state;

            if (activeItem !== itemsCount - 1) {
                this.setState({activeItem: activeItem + 1});
                this.scroll.scrollTop(this.scroll.getScrollTop() + 36);
            } else {
                this.setState({activeItem: 0});
                this.scroll.scrollToTop();
            }
        },
        Enter() {
            if (!this.scroll) {
                return;
            }

            const {items, activeItem} = this.state;

            this.handleSelect(items[activeItem]);
        }
    };

    handleClickOutsideSuggestList = event => {
        if (!this.state.listOpened) {
            return;
        }

        const {target} = event;

        if (this.field && !this.field.contains(target)) {
            if (this.suggestList && !this.suggestList.contains(target)) {
                this.setState({listOpened: false});
            }
        }
    };

    renderSuggestItems = () => {
        const {items, activeItem} = this.state;
        const {classes} = this.props;

        return items.map((item, index) => {
            return (
                <li
                    className={classNames('suggest__item', {
                        selected: index === activeItem,
                    })}
                    key={index}
                    onClick={event => this.handleSelect(event, item)}
                    onMouseOver={() => this.setState({activeItem: index})}
                    data-test="email-field-suggest-item"
                >
                    {item}
                </li>
            )
        })
    };

    handleSelect = (event, item) => {
        const {closeOnSelect, onChange} = this.props;
        this.setState(prevState => ({listOpened: closeOnSelect ? false : prevState.listOpened}));
        onChange(item, true);

        if (event.nativeEvent) {
            event.nativeEvent.stopImmediatePropagation();
        }
    };

    onChange = event => {
        const value = event.target.value;
        const {onChange} = this.props;

        onChange(value);

        const emails = this.getEmailsDomains(value);

        this.setState({
            items: emails,
            listOpened: !!emails.length,
            itemsCount: emails.length,
        });
    };

    onBlur = event => {
        const {onBlur} = this.props;

        this.setState({isActive: false});

        if (typeof onBlur === 'function') {
            onBlur(event);
        }
    };

    onFocus = event => {
        const {onFocus} = this.props;

        this.setState({isActive: true});

        if (typeof onFocus === 'function') {
            onFocus(event);
        }
    };

    onClick = event => {
        const {onClick} = this.props;

        if (typeof onClick === 'function') {
            onClick(event);
        }
    };

    handleRef = ref => {
        const {setRef} = this.props;

        this.field = ref;

        if (typeof setRef === 'function') {
            setRef(ref);
        }
    };

    render() {
        const {error, name, placeholder, className, visibleItems, disabled, classes, value, required, readOnly} = this.props;
        const {items, listOpened, isActive} = this.state;

        return (
            <div
                className={classNames({
                    //[classes.rootDefault]: true,
                    [classes.root]: true,
                    ['input-wrapper']: true,
                    filled: isActive || value,
                    focused: isActive,
                    disabled: disabled || readOnly,
                    error,
                    className
                })}
                data-test="email-root"
            >
                {!!error && !isActive && <div className={classes.error + " input__error"}>{error}</div>}
                <div style={{position: 'relative'}}>
                    <div className={classes.placeholder+ " input-placeholder"}>
                        {placeholder}{required && '*'}
                    </div>
                    <input
                        className={classNames({
                            ["input"]: true,
                            [classes.input]: !!classes.input,
                        })}
                        type="text"
                        name={name || 'email'}
                        disabled={disabled}
                        readOnly={readOnly}
                        value={value}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        onClick={this.onClick}
                        onChange={this.onChange}
                        ref={this.handleRef}
                        data-test="email-field"
                        onKeyDown={event => {
                            if (EmailField.keyDownHandlers[event.key]) {
                                EmailField.keyDownHandlers[event.key].call(this, event);
                            }
                        }}
                        autoComplete={isActive ? 'true' : 'off'}
                    />
                    {!!items.length && listOpened &&
                    <div className="suggest__suggesttions-container"
                         ref={ref => this.suggestList = ref}
                         data-test="email-field-suggest-container"
                    >
                        <Scrollbars
                            autoHeight
                            autoHeightMax={visibleItems * 36}
                            renderTrackVertical={props => items.length > visibleItems ?
                                <div {...props} className={classes.scrollTrack}/> : <div/>}
                            renderThumbVertical={props => items.length > visibleItems ?
                                <div {...props} className={classes.scrollThumb}/> : <div/>}
                            ref={ref => this.scroll = ref}
                        >
                            <ul>
                                {this.renderSuggestItems()}
                            </ul>
                        </Scrollbars>
                    </div>
                    }
                </div>
                {!!error && !isActive && <div className={classes.fakeError}>{error}</div>}
            </div>
        )
    }
}

export default EmailField;

EmailField.defaultProps = {
    closeOnSelect: true,
    visibleItems: 5,
    className: '',
    required: false,
    classes: {},
};

EmailField.propTypes = {
    placeholder: PropTypes.string.isRequired,
    closeOnSelect: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    error: PropTypes.string,
    setRef: PropTypes.func,
    visibleItems: PropTypes.number,
    className: PropTypes.string,
    value: PropTypes.string.isRequired,
    required: PropTypes.bool,
    readOnly: PropTypes.bool
};
